import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { ThemePayload, useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { themes as themesApi } from "../../services/api";

export const isValidHex = (value: string): boolean => /^#[0-9a-fA-F]{6}$/.test(value);

const toSlug = (label: string): string => label.toLowerCase().trim().replace(/\s+/g, "-");

// Theme entity columns whose value comes from a form <select> (kebab-case on the
// backend) rather than free text — used both to slugify on save and un-slugify on load.
const SLUG_CONFIG_FIELDS = ["layoutStyle", "securityMode", "analyticsScope", "accessLevel", "buttonStyle"] as const;

export interface ThemeField {
  name: string;
  label: string;
  type: "text" | "textarea" | "select" | "color";
  placeholder?: string;
  helper?: string;
  options?: string[];
  required: boolean;
}

interface ThemePageShellProps {
  title: string;
  subtitle: string;
  description: string;
  featureList: string[];
  fields: ThemeField[];
  cta: string;
  pathLabel: string;
  themePayload: ThemePayload;
  /** Derives live ThemePayload overrides from the submitted form values, for
   *  fields that map to a visual property (accent color, sidebar/topbar color, etc.).
   *  Returned keys are merged onto themePayload and applied via setTheme. */
  getLiveOverrides?: (values: Record<string, string>) => Partial<ThemePayload>;
  /** Names of the fields in `fields` that drive `getLiveOverrides`, so the
   *  "Reset to defaults" control knows which ones to clear and revert. */
  liveFieldNames?: string[];
  /** Stable identifier for this theme page (e.g. "executive-suite"), combined with
   *  the society ID to form a unique, per-society Theme row slug on the backend. */
  baseSlug: string;
}

const THEME_LS_KEY = "nexora-selected-theme";
const configKey = (title: string) => `nexora-theme-config:${title}`;

const ThemePageShell: React.FC<ThemePageShellProps> = ({
  title,
  subtitle,
  description,
  featureList,
  fields,
  cta,
  pathLabel,
  themePayload,
  getLiveOverrides,
  liveFieldNames,
  baseSlug,
}) => {
  const { setTheme } = useTheme();
  const { user } = useAuth();
  const societyId = user?.societyId;
  const initialValues = useMemo(
    () =>
      fields.reduce(
        (acc, field) => {
          // Color fields show their default as a live swatch/hex readout before the
          // user touches them, so seed the real value too — otherwise the field looks
          // filled in but is actually empty and fails required-field validation.
          acc[field.name] = field.type === "color" ? (field.placeholder ?? "") : "";
          return acc;
        },
        {} as Record<string, string>,
      ),
    [fields],
  );

  useEffect(() => {
    setTheme(themePayload);
    try {
      window.localStorage.setItem(
        THEME_LS_KEY,
        JSON.stringify({ name: title, path: window.location.pathname }),
      );
    } catch {
      // ignore storage failures
    }
  }, [title]); // eslint-disable-line react-hooks/exhaustive-deps

  // Repairs values that ended up invalid — e.g. from a stale localStorage snapshot
  // saved before color fields had real defaults, or a "name" that's actually our
  // own internal cache key (a since-fixed bug that could have gotten persisted).
  const repairValues = (input: Record<string, string>): Record<string, string> => {
    const next = { ...input };
    fields.forEach((field) => {
      if (field.type === "color" && !isValidHex(next[field.name])) {
        next[field.name] = field.placeholder ?? next[field.name];
      }
    });
    if (next.themeName?.startsWith("nexora-theme-config:")) {
      next.themeName = title;
    }
    return next;
  };

  const [values, setValues] = useState<Record<string, string>>(() => {
    try {
      const saved = window.localStorage.getItem(configKey(title));
      const merged = saved ? { ...initialValues, ...JSON.parse(saved) } : initialValues;
      return repairValues(merged);
    } catch {
      return initialValues;
    }
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [serverSynced, setServerSynced] = useState(false);
  const [existingThemeId, setExistingThemeId] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Load this society's own saved copy of this theme, if one exists, so the form
  // (and the live palette) reflect what's actually persisted server-side rather
  // than just this browser's localStorage cache.
  useEffect(() => {
    if (!societyId) {
      setInitialLoading(false);
      return;
    }
    const slug = `${baseSlug}--${societyId}`;
    themesApi
      .list(societyId)
      .then((list) => {
        const existing = list.find((t) => t.slug === slug);
        if (!existing) return;
        setExistingThemeId(existing.id);
        setServerSynced(true);
        setValues((prev) => {
          let next = { ...prev };
          if ("themeName" in next) next.themeName = existing.name;
          if ("primaryAccent" in next) next.primaryAccent = existing.primaryAccent;
          if ("sidebarColor" in next) next.sidebarColor = existing.sidebarBackground;
          if ("sidebarTextColor" in next) next.sidebarTextColor = existing.sidebarTextColor;
          if ("topbarColor" in next) next.topbarColor = existing.topbarBackground;
          if ("topbarTextColor" in next) next.topbarTextColor = existing.topbarTextColor;
          if ("ctaText" in next && existing.ctaText) next.ctaText = existing.ctaText;
          SLUG_CONFIG_FIELDS.forEach((name) => {
            if (!(name in next)) return;
            const stored = (existing as unknown as Record<string, string | undefined>)[name];
            if (!stored) return;
            const field = fields.find((f) => f.name === name);
            const match = field?.options?.find((opt) => toSlug(opt) === stored);
            next[name] = match ?? next[name];
          });
          next = repairValues(next);
          try {
            window.localStorage.setItem(configKey(title), JSON.stringify(next));
          } catch {
            // ignore storage failures
          }
          return next;
        });
        setTheme({
          sidebarBackground: existing.sidebarBackground,
          sidebarTextColor: existing.sidebarTextColor,
          topbarBackground: existing.topbarBackground,
          topbarTextColor: existing.topbarTextColor,
          primaryAccent: existing.primaryAccent,
          surface: existing.surface,
        });
        // Clear any validation error left over from a submit made before this
        // server data finished loading — the fields it complained about are
        // filled in now.
        setError("");
      })
      .catch(() => {
        // no saved copy yet, or request failed — form keeps its local/default values
      })
      .finally(() => setInitialLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societyId, baseSlug]);

  const onChange = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const buildThemeRequest = (formValues: Record<string, string>, palette: ThemePayload) => {
    const payload: Record<string, unknown> = {
      name: formValues.themeName?.trim() || title,
      slug: `${baseSlug}--${societyId}`,
      sidebarBackground: palette.sidebarBackground,
      sidebarTextColor: palette.sidebarTextColor,
      topbarBackground: palette.topbarBackground,
      topbarTextColor: palette.topbarTextColor,
      primaryAccent: palette.primaryAccent,
      surface: palette.surface,
    };
    if (formValues.ctaText?.trim()) payload.ctaText = formValues.ctaText.trim();
    SLUG_CONFIG_FIELDS.forEach((name) => {
      const raw = formValues[name];
      if (raw?.trim()) payload[name] = toSlug(raw);
    });
    return payload;
  };

  // Upserts this society's copy of the theme on the backend and activates it,
  // so the colors persist across devices/browsers instead of just this one.
  // `formValues` is threaded through explicitly rather than closing over `values`,
  // since callers (like reset) need this to reflect a value they just set that
  // hasn't flushed to state yet.
  const persistTheme = async (palette: ThemePayload, formValues: Record<string, string>) => {
    if (!societyId) return;
    setSaving(true);
    try {
      const req = buildThemeRequest(formValues, palette);
      const saved = existingThemeId
        ? await themesApi.update(societyId, existingThemeId, req)
        : await themesApi.create(societyId, req);
      setExistingThemeId(saved.id);
      await themesApi.activate(societyId, saved.id);
      setServerSynced(true);
    } catch {
      setServerSynced(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const missing = fields.filter(
      (field) => field.required && !values[field.name].trim(),
    );
    if (missing.length) {
      setError(
        `Please complete ${missing.map((field) => field.label).join(", ")}.`,
      );
      setSubmitted(false);
      return;
    }
    setError("");
    try {
      window.localStorage.setItem(configKey(title), JSON.stringify(values));
    } catch {
      // ignore storage failures
    }
    const overrides = getLiveOverrides?.(values);
    const palette = overrides && Object.keys(overrides).length > 0 ? { ...themePayload, ...overrides } : themePayload;
    setTheme(palette);
    void persistTheme(palette, values);
    setSubmitted(true);
  };

  const resetLiveFields = () => {
    if (!liveFieldNames?.length) return;
    const resetValues = { ...values };
    liveFieldNames.forEach((name) => {
      resetValues[name] = initialValues[name] ?? "";
    });
    setValues(resetValues);
    setTheme(themePayload);
    void persistTheme(themePayload, resetValues);
    setSubmitted(false);
    setError("");
    try {
      window.localStorage.setItem(configKey(title), JSON.stringify(resetValues));
    } catch {
      // ignore storage failures
    }
  };

  return (
    <div
      style={{
        background: "var(--nx-surface-2)",
        minHeight: "100vh",
        fontFamily: "var(--font-body)",
      }}
    >
      <Navbar variant="dashboard" />
      <Sidebar />

      <main
        style={{
          marginLeft: 220,
          marginTop: 60,
          padding: "32px 32px 48px",
          maxWidth: 1200,
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 24,
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 28,
          }}
        >
          <div style={{ flex: "1 1 560px", minWidth: 280 }}>
            <span
              className="nx-badge nx-badge-violet"
              style={{ marginBottom: 16, display: "inline-flex" }}
            >
              {pathLabel}
            </span>
            <h1
              style={{
                fontFamily: "Syne, sans-serif",
                fontSize: "clamp(32px, 44px, 52px)",
                marginBottom: 12,
                color: "var(--nx-text-primary)",
              }}
            >
              {title}
            </h1>
            <p
              style={{
                fontSize: 15,
                color: "var(--nx-text-secondary)",
                maxWidth: 620,
                lineHeight: 1.8,
              }}
            >
              {subtitle}
            </p>
          </div>

          <div
            style={{
              minWidth: 260,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div
              style={{
                background: "var(--nx-surface-1)",
                border: "1px solid var(--nx-border)",
                borderRadius: 16,
                padding: 18,
                boxShadow: "var(--shadow-sm)",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "var(--nx-text-muted)",
                  marginBottom: 10,
                }}
              >
                Key benefits
              </div>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: 0,
                  display: "grid",
                  gap: 10,
                }}
              >
                {featureList.map((item) => (
                  <li
                    key={item}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      color: "var(--nx-text-secondary)",
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "var(--nx-blue)",
                        flexShrink: 0,
                        marginTop: 4,
                      }}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.4fr 1fr",
            gap: 24,
            alignItems: "start",
          }}
        >
          <section
            className="nx-card"
            style={{ padding: 32, borderRadius: 24 }}
          >
            <h2
              style={{
                fontSize: 18,
                marginBottom: 18,
                color: "var(--nx-text-primary)",
              }}
            >
              Theme configuration
            </h2>
            <p
              style={{
                fontSize: 14,
                color: "var(--nx-text-secondary)",
                marginBottom: 24,
              }}
            >
              {description}
            </p>
            <form onSubmit={handleSubmit} noValidate>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
                  gap: "16px 20px",
                  alignItems: "start",
                }}
              >
                {fields.map((field) => (
                  <div
                    key={field.name}
                    style={field.type === "textarea" ? { gridColumn: "1 / -1" } : undefined}
                  >
                    <label
                      htmlFor={field.name}
                      style={{
                        display: "block",
                        fontSize: 12,
                        fontWeight: 600,
                        marginBottom: 6,
                        color: "var(--nx-text-primary)",
                      }}
                    >
                      {field.label}
                      {field.required && (
                        <span style={{ color: "#EF4444" }}> *</span>
                      )}
                    </label>

                    {field.type === "textarea" ? (
                      <textarea
                        id={field.name}
                        value={values[field.name]}
                        placeholder={field.placeholder}
                        onChange={(event) =>
                          onChange(field.name, event.target.value)
                        }
                        style={{
                          width: "100%",
                          minHeight: 72,
                          border: "1px solid var(--nx-border)",
                          borderRadius: 12,
                          padding: "10px 12px",
                          fontSize: 13,
                          fontFamily: "inherit",
                          color: "var(--nx-text-primary)",
                          background: "var(--nx-surface-3)",
                        }}
                      />
                    ) : field.type === "select" ? (
                      <select
                        id={field.name}
                        value={values[field.name]}
                        onChange={(event) =>
                          onChange(field.name, event.target.value)
                        }
                        style={{
                          width: "100%",
                          border: "1px solid var(--nx-border)",
                          borderRadius: 12,
                          padding: "10px 12px",
                          fontSize: 13,
                          fontFamily: "inherit",
                          color: "var(--nx-text-primary)",
                          background: "var(--nx-surface-3)",
                        }}
                      >
                        <option value="" disabled>
                          {field.placeholder || "Choose an option"}
                        </option>
                        {field.options?.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : field.type === "color" ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <input
                          id={field.name}
                          type="color"
                          value={/^#[0-9a-fA-F]{6}$/.test(values[field.name]) ? values[field.name] : (field.placeholder || "#1A6CF6")}
                          onChange={(event) =>
                            onChange(field.name, event.target.value)
                          }
                          style={{
                            width: 40,
                            height: 38,
                            padding: 0,
                            border: "1px solid var(--nx-border)",
                            borderRadius: 8,
                            background: "none",
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                        />
                        <input
                          type="text"
                          value={values[field.name]}
                          placeholder={field.placeholder}
                          onChange={(event) =>
                            onChange(field.name, event.target.value)
                          }
                          style={{
                            flex: 1,
                            minWidth: 0,
                            border: "1px solid var(--nx-border)",
                            borderRadius: 12,
                            padding: "10px 12px",
                            fontSize: 13,
                            fontFamily: "inherit",
                            color: "var(--nx-text-primary)",
                            background: "var(--nx-surface-3)",
                          }}
                        />
                      </div>
                    ) : (
                      <input
                        id={field.name}
                        type={field.type}
                        value={values[field.name]}
                        placeholder={field.placeholder}
                        onChange={(event) =>
                          onChange(field.name, event.target.value)
                        }
                        style={{
                          width: "100%",
                          border: "1px solid var(--nx-border)",
                          borderRadius: 12,
                          padding: "10px 12px",
                          fontSize: 13,
                          fontFamily: "inherit",
                          color: "var(--nx-text-primary)",
                          background: "var(--nx-surface-3)",
                        }}
                      />
                    )}

                    {field.helper && (
                      <div
                        style={{
                          marginTop: 5,
                          fontSize: 11,
                          color: "var(--nx-text-muted)",
                        }}
                      >
                        {field.helper}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {error && (
                <div
                  style={{
                    marginTop: 18,
                    padding: "14px 16px",
                    background: "rgba(239,68,68,0.08)",
                    borderRadius: 12,
                    color: "#991B1B",
                  }}
                >
                  {error}
                </div>
              )}
              {submitted && !error && (
                <div
                  style={{
                    marginTop: 18,
                    padding: "14px 16px",
                    background: "rgba(16,185,129,0.1)",
                    borderRadius: 12,
                    color: "#047857",
                  }}
                >
                  {serverSynced
                    ? "Theme saved to your society's account — it'll apply for everyone and follow you across devices."
                    : "Theme applied on this device, but saving to your account failed. Try again in a moment."}
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 24 }}>
                <button type="submit" className="nx-btn nx-btn-primary" disabled={saving || initialLoading}>
                  {saving ? <Loader2 size={14} style={{ animation: "nx-spin 0.7s linear infinite" }} /> : <ArrowRight size={14} />}
                  {saving ? "Saving…" : cta}
                </button>
                {!!liveFieldNames?.length && (
                  <button
                    type="button"
                    className="nx-btn nx-btn-outline"
                    onClick={resetLiveFields}
                    disabled={saving || initialLoading}
                  >
                    Reset to default colors
                  </button>
                )}
              </div>
            </form>
          </section>

          <aside
            className="nx-card"
            style={{
              padding: 32,
              borderRadius: 24,
              background:
                "linear-gradient(180deg, rgba(26,108,246,0.08), rgba(255,255,255,0.72))",
            }}
          >
            <h3 style={{ marginBottom: 16, color: "var(--nx-text-primary)" }}>
              Security-first design
            </h3>
            <p
              style={{
                fontSize: 14,
                color: "var(--nx-text-secondary)",
                lineHeight: 1.8,
              }}
            >
              Each premium theme page includes the fields needed to enforce
              strong design, secure access controls, and backend-ready payloads.
              Use these fields to drive your API contract and frontend
              validation.
            </p>
            <ul
              style={{
                marginTop: 20,
                listStyle: "none",
                padding: 0,
                display: "grid",
                gap: 12,
              }}
            >
              <li
                style={{ display: "flex", gap: 10, alignItems: "flex-start" }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "var(--nx-blue)",
                    marginTop: 5,
                  }}
                />
                Secure form schema with required validation
              </li>
              <li
                style={{ display: "flex", gap: 10, alignItems: "flex-start" }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "var(--nx-blue)",
                    marginTop: 5,
                  }}
                />
                Minimal page navigation and explicit theme ownership
              </li>
              <li
                style={{ display: "flex", gap: 10, alignItems: "flex-start" }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "var(--nx-blue)",
                    marginTop: 5,
                  }}
                />
                Ready for backend API hardened by validation, auth, and logging
              </li>
            </ul>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default ThemePageShell;
